/**
 * FN-ALL-01..05 — Teljes Költségallokáció UI
 * Tab 0: Allokációk (bejövő számlák / tétel-szintű allokáció)
 * Tab 1: Szabályok (automatikus allokációs szabályok, dry-run, reapply, sorrend)
 * Tab 2: Sablonok (allokációs sablonok CRUD)
 * Tab 3: Projektköltség (KPI + breakdown + export)
 */
import { useState, useEffect, useCallback } from 'react';
import { get, post, patch, del } from '../../lib/api';
import { PageHeader, Button, Badge, Card, Input, Modal, EmptyState } from '../../components/ui';
import { useToast } from '../../components/ui/Toast';

// ── Types ──

interface IncomingInvoice {
  id: string;
  supplier_invoice_number: string;
  supplier_name?: string;
  partner_name?: string;
  issue_date?: string;
  subtotal_net?: number;   // HUF (converted by service)
  subtotal_gross?: number; // HUF
  status: string;
  lines?: InvoiceLine[];
}

interface InvoiceLine {
  id: string;
  description: string;
  line_number: number;
  line_net: number;   // HUF
  line_vat: number;   // HUF
  line_gross: number; // HUF
  quantity: number;
  unit_price_net: number; // HUF
  vat_rate: string;
}

interface Allocation {
  id: string;
  incoming_invoice_line_id: string;
  project_id: string;
  project_name: string;
  mode: string;
  value: number | null;
  allocated_net_amount: number;   // fillers (raw DB)
  allocated_vat_amount: number;   // fillers
  allocated_gross_amount: number; // fillers
  line_description?: string;
  line_number?: number;
  notes?: string;
}

interface AllocationRule {
  id: string;
  name: string;
  condition_type: string;
  condition_value: string | null;
  sort_order: number;
  active: boolean;
  targets: RuleTarget[];
}

interface RuleTarget {
  target_type: string;
  target_id: string;
  target_name: string;
  percentage: number | null;
  fixed_amount: number | null;
}

interface AllocationTemplate {
  id: string;
  name: string;
  description?: string;
  template_json: {
    allocations: Array<{ projectId: string; mode: string; value?: number }>;
  };
  created_at: string;
}

interface Project {
  id: string;
  name: string;
  project_code: string;
}

interface CostReport {
  project: { name: string; partner: string | null; status: string };
  revenue: {
    totalGross: number;
    totalNet: number;
    totalVat: number;
    byInvoice: Array<{ invoiceNumber: string; net: number; gross: number }>;
  };
  costs: {
    directAllocated: {
      net: number; vat: number; gross: number;
      byIncomingInvoice: Array<{
        invoiceNumber: string; supplierName: string;
        allocations: Array<{ lineDescription: string; amount: number; mode: string; value: number | null }>;
      }>;
    };
    laborCost: {
      hours: number; totalCost: number; billableHours: number; nonBillableHours: number;
      byUser: Array<{ userName: string; hours: number; cost: number }>;
    };
    totalCost: number;
  };
  margin: { amount: number | null; percent: number | null };
  generatedAt: string;
}

// ── Helpers ──

function fmt(v: number | undefined | null) {
  if (v === undefined || v === null) return '—';
  return new Intl.NumberFormat('hu-HU').format(Math.round(v)) + ' Ft';
}

function fmtDate(iso: string | undefined) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('hu-HU', { year: 'numeric', month: 'short', day: 'numeric' }); }
  catch { return '—'; }
}

const MODE_LABELS: Record<string, string> = {
  full: 'Teljes', percent: '%', fixed_amount: 'Fix Ft', quantity: 'db', time_proportional: 'Időarányos',
};

function modeDisplay(mode: string, value: number | null): string {
  if (mode === 'full') return 'Teljes';
  if (mode === 'percent') return `${value}%`;
  if (mode === 'fixed_amount') return `Fix ${value ? new Intl.NumberFormat('hu-HU').format(value) + ' Ft' : '—'}`;
  if (mode === 'quantity') return `${value} db`;
  if (mode === 'time_proportional') return 'Időarányos';
  return mode;
}

const COND_LABELS: Record<string, string> = {
  always: 'Mindig', supplier: 'Beszállító neve', description: 'Tárgy tartalmaz', amount_range: 'Összeg-tartomány',
};

// ════════════════════════════════════════════
// Main Page
// ════════════════════════════════════════════

export default function AllocationsPage() {
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState(0);
  const [projects, setProjects] = useState<Project[]>([]);

  useEffect(() => {
    get<any>('/projects')
      .then(d => setProjects(Array.isArray(d) ? d : d?.data || []))
      .catch(() => {});
  }, []);

  const TABS = ['Allokációk', 'Szabályok', 'Sablonok', 'Projektköltség'];

  return (
    <div>
      <PageHeader
        title="Költségallokáció"
        description="Bejövő számlák tételeinek szétosztása projektek között (Pro)"
      />

      {/* Tab bar */}
      <div style={{
        display: 'flex', gap: 0, marginBottom: '20px',
        borderBottom: '2px solid var(--line)', flexWrap: 'wrap',
      }}>
        {TABS.map((label, i) => (
          <button key={i} onClick={() => setActiveTab(i)} style={{
            padding: '9px 18px', border: 'none', background: 'none', cursor: 'pointer',
            borderBottom: activeTab === i ? '2px solid var(--orange)' : '2px solid transparent',
            marginBottom: '-2px', fontWeight: activeTab === i ? 700 : 400,
            color: activeTab === i ? 'var(--orange)' : 'var(--ink)', fontSize: '14px',
            transition: 'color 0.15s',
          }}>{label}</button>
        ))}
      </div>

      {activeTab === 0 && <AllocationTab toast={toast} projects={projects} />}
      {activeTab === 1 && <RulesTab toast={toast} projects={projects} />}
      {activeTab === 2 && <TemplatesTab toast={toast} projects={projects} />}
      {activeTab === 3 && <ReportTab toast={toast} projects={projects} />}
    </div>
  );
}

// ════════════════════════════════════════════
// Tab 0 — Allokációk (bejövő számla lista)
// ════════════════════════════════════════════

function AllocationTab({ toast, projects }: {
  toast: (m: string, v: string) => void;
  projects: Project[];
}) {
  const [invoices, setInvoices] = useState<IncomingInvoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [detailInvoice, setDetailInvoice] = useState<IncomingInvoice | null>(null);
  const [templates, setTemplates] = useState<AllocationTemplate[]>([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await get<any>('/incoming-invoices');
      setInvoices(Array.isArray(d) ? d : d?.data || []);
    } catch { /* silent */ }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, []);

  useEffect(() => {
    get<any>('/allocation-templates')
      .then(d => setTemplates(Array.isArray(d) ? d : []))
      .catch(() => {});
  }, []);

  const filtered = invoices.filter(inv => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (
      inv.supplier_invoice_number?.toLowerCase().includes(s) ||
      (inv.partner_name || inv.supplier_name || '').toLowerCase().includes(s)
    );
  });

  const openInvoice = async (inv: IncomingInvoice) => {
    try {
      const full = await get<IncomingInvoice>(`/incoming-invoices/${inv.id}`);
      setDetailInvoice(full);
    } catch { toast('Számla betöltése sikertelen.', 'error'); }
  };

  if (loading) return (
    <div style={{ textAlign: 'center', padding: '40px', color: 'var(--slate)' }}>Betöltés...</div>
  );

  if (invoices.length === 0) return (
    <EmptyState
      icon="📥"
      title="Nincs bejövő számla"
      description="Rögzíts vagy szinkronizálj bejövő számlákat az allokáláshoz."
    />
  );

  return (
    <div>
      <div style={{ marginBottom: '14px' }}>
        <Input
          placeholder="Keresés sorszám, beszállító..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ maxWidth: '360px' }}
        />
      </div>

      <div style={{ display: 'grid', gap: '8px' }}>
        {filtered.map(inv => {
          const supplierName = inv.partner_name || inv.supplier_name || '—';
          return (
            <Card key={inv.id} style={{ padding: '12px 16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: '14px', color: 'var(--navy)', textTransform: 'uppercase', letterSpacing: '0.3px' }}>
                    {inv.supplier_invoice_number}
                  </div>
                  <div style={{ fontSize: '13px', color: 'var(--ink)', marginTop: '2px' }}>{supplierName}</div>
                  <div style={{ fontSize: '12px', color: 'var(--slate)', marginTop: '2px' }}>{fmtDate(inv.issue_date)}</div>
                </div>
                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                  <div style={{ fontWeight: 800, fontSize: '16px', color: 'var(--navy)' }}>{fmt(inv.subtotal_gross)}</div>
                  {inv.subtotal_net !== undefined && (
                    <div style={{ fontSize: '11px', color: 'var(--slate)' }}>nettó: {fmt(inv.subtotal_net)}</div>
                  )}
                </div>
                <Button size="sm" onClick={() => openInvoice(inv)}>Allokálás →</Button>
              </div>
            </Card>
          );
        })}
      </div>

      {filtered.length === 0 && invoices.length > 0 && (
        <div style={{ textAlign: 'center', padding: '30px', color: 'var(--slate)', fontSize: '13px' }}>
          Nincs találat a keresésre.
        </div>
      )}

      {detailInvoice && (
        <InvoiceDetailModal
          invoice={detailInvoice}
          projects={projects}
          templates={templates}
          onClose={() => { setDetailInvoice(null); load(); }}
          toast={toast}
        />
      )}
    </div>
  );
}

// ── Invoice Detail Modal ──

function InvoiceDetailModal({ invoice, projects, templates, onClose, toast }: {
  invoice: IncomingInvoice;
  projects: Project[];
  templates: AllocationTemplate[];
  onClose: () => void;
  toast: (m: string, v: string) => void;
}) {
  const [lines, setLines] = useState<InvoiceLine[]>([]);
  const [allocations, setAllocations] = useState<Allocation[]>([]);
  const [loading, setLoading] = useState(true);
  const [addForLine, setAddForLine] = useState<InvoiceLine | null>(null);
  const [tpForLine, setTpForLine] = useState<InvoiceLine | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const full = await get<IncomingInvoice & { lines: InvoiceLine[] }>(`/incoming-invoices/${invoice.id}`);
      setLines(full.lines || []);
      const allocs = await get<Allocation[]>(`/incoming-invoices/${invoice.id}/allocations`);
      setAllocations(Array.isArray(allocs) ? allocs : []);
    } catch { toast('Betöltés sikertelen.', 'error'); }
    setLoading(false);
  }, [invoice.id]);

  useEffect(() => { reload(); }, []);

  const supplierName = invoice.partner_name || invoice.supplier_name || '—';
  // invoice.subtotal_net is in HUF; allocation amounts are in fillers
  const totalNetHuf = invoice.subtotal_net || 0;
  const allocatedNetFillers = allocations.reduce((s, a) => s + Number(a.allocated_net_amount), 0);
  const allocatedNetHuf = allocatedNetFillers / 100;
  const pct = totalNetHuf > 0 ? Math.min(100, Math.round(allocatedNetHuf / totalNetHuf * 100)) : 0;

  return (
    <Modal isOpen={true} onClose={onClose} title={`Allokáció — ${invoice.supplier_invoice_number}`} size="xl">
      <div style={{ maxHeight: '78vh', overflowY: 'auto', paddingRight: '2px' }}>
        {/* Header summary */}
        <div style={{ background: 'var(--mist)', borderRadius: 'var(--r)', padding: '12px 16px', marginBottom: '16px' }}>
          <div style={{ fontWeight: 700, fontSize: '14px', color: 'var(--navy)', marginBottom: '4px' }}>{supplierName}</div>
          <div style={{ display: 'flex', gap: '16px', fontSize: '13px', color: 'var(--slate)', flexWrap: 'wrap' }}>
            <span>Kelt: {fmtDate(invoice.issue_date)}</span>
            <span>Nettó összesen: <strong style={{ color: 'var(--navy)' }}>{fmt(totalNetHuf)}</strong></span>
            <span>Allokált nettó: <strong style={{ color: '#16a34a' }}>{fmt(allocatedNetHuf)}</strong> ({pct}%)</span>
          </div>
          {totalNetHuf > 0 && (
            <div style={{ marginTop: '8px', height: '5px', background: 'var(--line)', borderRadius: '3px', overflow: 'hidden' }}>
              <div style={{
                height: '100%', width: `${pct}%`,
                background: pct >= 100 ? '#16a34a' : 'var(--orange)',
                borderRadius: '3px', transition: 'width 0.3s',
              }} />
            </div>
          )}
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '30px', color: 'var(--slate)' }}>Betöltés...</div>
        ) : lines.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '20px', color: 'var(--slate)', fontSize: '13px' }}>
            Nincs számlasor.
          </div>
        ) : (
          <div style={{ display: 'grid', gap: '10px' }}>
            {lines.map(line => {
              const lineAllocs = allocations.filter(a => a.incoming_invoice_line_id === line.id);
              // line.line_net is HUF; allocated_net_amount is fillers
              const lineAllocatedHuf = lineAllocs.reduce((s, a) => s + Number(a.allocated_net_amount), 0) / 100;
              const linePct = line.line_net > 0 ? Math.min(100, Math.round(lineAllocatedHuf / line.line_net * 100)) : 0;
              const isFullyAllocated = linePct >= 100;

              return (
                <div key={line.id} style={{
                  border: `1px solid ${isFullyAllocated ? '#bbf7d0' : 'var(--line)'}`,
                  borderRadius: 'var(--r)', padding: '12px',
                  background: isFullyAllocated ? '#f0fdf4' : '#fff',
                }}>
                  {/* Line header */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px', marginBottom: '8px', flexWrap: 'wrap' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 700, fontSize: '13px', color: 'var(--navy)' }}>
                        {line.line_number}. {line.description}
                      </div>
                      <div style={{ fontSize: '12px', color: 'var(--slate)', marginTop: '2px' }}>
                        {line.quantity} × {fmt(line.unit_price_net)} | nettó: {fmt(line.line_net)} | bruttó: {fmt(line.line_gross)}
                      </div>
                      {line.line_net > 0 && (
                        <div style={{ marginTop: '5px', height: '3px', background: 'var(--line)', borderRadius: '2px', maxWidth: '200px' }}>
                          <div style={{ height: '100%', width: `${linePct}%`, background: isFullyAllocated ? '#16a34a' : 'var(--orange)', borderRadius: '2px' }} />
                        </div>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: '5px', flexShrink: 0, flexWrap: 'wrap' }}>
                      {!isFullyAllocated && (
                        <Button size="sm" onClick={() => setAddForLine(line)}>+ Allokáció</Button>
                      )}
                      <Button size="sm" variant="ghost" onClick={() => setTpForLine(line)} title="Időarányos elosztás">⏱ Időarányos</Button>
                      {templates.length > 0 && (
                        <TemplatePicker
                          templates={templates}
                          onApply={async (tplId) => {
                            try {
                              await post(`/incoming-invoice-lines/${line.id}/apply-template/${tplId}`, {});
                              toast('Sablon alkalmazva.', 'success');
                              reload();
                            } catch (e: any) { toast(e?.message || 'Hiba.', 'error'); }
                          }}
                        />
                      )}
                    </div>
                  </div>

                  {/* Existing allocations */}
                  {lineAllocs.length > 0 ? (
                    <div style={{ display: 'grid', gap: '4px' }}>
                      {lineAllocs.map(a => (
                        <div key={a.id} style={{
                          display: 'flex', alignItems: 'center', gap: '8px',
                          background: 'rgba(0,0,0,0.03)', borderRadius: '4px', padding: '6px 10px', fontSize: '12px',
                        }}>
                          <span style={{ flex: 1, fontWeight: 700, color: 'var(--navy)' }}>{a.project_name}</span>
                          <Badge variant="info">{modeDisplay(a.mode, a.value)}</Badge>
                          <span style={{ fontWeight: 700, color: '#16a34a' }}>
                            {fmt(Number(a.allocated_net_amount) / 100)} nettó
                          </span>
                          <button
                            onClick={async () => {
                              try {
                                await del(`/cost-allocations/${a.id}`);
                                toast('Allokáció törölve.', 'success');
                                reload();
                              } catch { toast('Törlés sikertelen.', 'error'); }
                            }}
                            style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: '15px', lineHeight: 1, padding: '0 4px' }}
                            title="Törlés"
                          >×</button>
                        </div>
                      ))}
                      <div style={{ fontSize: '11px', color: 'var(--slate)', textAlign: 'right' }}>
                        {fmt(lineAllocatedHuf)} / {fmt(line.line_net)} allokálva ({linePct}%)
                      </div>
                    </div>
                  ) : (
                    <div style={{ fontSize: '12px', color: 'var(--slate)', fontStyle: 'italic' }}>
                      Még nincs allokáció erre a sorra.
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', paddingTop: '14px', borderTop: '1px solid var(--line)', marginTop: '14px' }}>
        <Button variant="ghost" onClick={onClose}>Bezárás</Button>
      </div>

      {addForLine && (
        <AddAllocationModal
          line={addForLine}
          projects={projects}
          allocations={allocations.filter(a => a.incoming_invoice_line_id === addForLine.id)}
          onClose={() => setAddForLine(null)}
          onSaved={() => { setAddForLine(null); reload(); }}
          toast={toast}
        />
      )}

      {tpForLine && (
        <TimeProportionalModal
          line={tpForLine}
          projects={projects}
          onClose={() => setTpForLine(null)}
          onSaved={() => { setTpForLine(null); reload(); }}
          toast={toast}
        />
      )}
    </Modal>
  );
}

// ── Template Picker (dropdown) ──

function TemplatePicker({ templates, onApply }: {
  templates: AllocationTemplate[];
  onApply: (id: string) => void;
}) {
  const [open, setOpen] = useState(false);

  return (
    <div style={{ position: 'relative' }}>
      <Button size="sm" variant="ghost" onClick={() => setOpen(o => !o)} title="Sablon alkalmazása">
        📋 Sablon
      </Button>
      {open && (
        <>
          <div onClick={() => setOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 49 }} />
          <div style={{
            position: 'absolute', right: 0, top: '100%', background: '#fff',
            border: '1px solid var(--line)', borderRadius: 'var(--r)',
            boxShadow: '0 4px 16px rgba(0,0,0,0.12)', zIndex: 50, minWidth: '200px',
            padding: '4px 0', marginTop: '4px',
          }}>
            <div style={{ fontSize: '10px', color: 'var(--slate)', fontWeight: 700, padding: '4px 12px 2px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Sablon választása
            </div>
            {templates.map(t => (
              <button key={t.id} onClick={() => { setOpen(false); onApply(t.id); }} style={{
                display: 'block', width: '100%', textAlign: 'left', padding: '8px 12px',
                border: 'none', background: 'none', cursor: 'pointer', fontSize: '13px', color: 'var(--ink)',
              }}
                onMouseEnter={e => (e.currentTarget.style.background = 'var(--mist)')}
                onMouseLeave={e => (e.currentTarget.style.background = 'none')}
              >{t.name}</button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ── Add Allocation Modal ──

function AddAllocationModal({ line, projects, allocations, onClose, onSaved, toast }: {
  line: InvoiceLine;
  projects: Project[];
  allocations: Allocation[]; // existing allocations for this line
  onClose: () => void;
  onSaved: () => void;
  toast: (m: string, v: string) => void;
}) {
  const [projectId, setProjectId] = useState('');
  const [mode, setMode] = useState<'full' | 'percent' | 'fixed_amount' | 'quantity'>('full');
  const [value, setValue] = useState<number | ''>('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  // line.line_net is HUF; calculate remaining
  const alreadyAllocatedHuf = allocations.reduce((s, a) => s + Number(a.allocated_net_amount), 0) / 100;
  const remainingHuf = line.line_net - alreadyAllocatedHuf;

  function calcPreviewHuf(): number | null {
    switch (mode) {
      case 'full': return remainingHuf;
      case 'percent': return value !== '' ? Math.round(line.line_net * Number(value) / 100) : null;
      case 'fixed_amount': return value !== '' ? Number(value) : null;
      case 'quantity': return value !== '' ? Math.round(Number(value) * line.unit_price_net) : null;
      default: return null;
    }
  }

  const previewHuf = calcPreviewHuf();

  const isValid = () => {
    if (!projectId) return false;
    if (mode !== 'full' && (value === '' || Number(value) <= 0)) return false;
    if (mode === 'percent' && Number(value) > 100) return false;
    if (mode === 'fixed_amount' && Number(value) > remainingHuf + 0.01) return false;
    return true;
  };

  const handleSave = async () => {
    if (!isValid()) return;
    setSaving(true);
    try {
      await post(`/incoming-invoice-lines/${line.id}/allocations`, {
        projectId,
        mode,
        value: mode === 'full' ? null : value !== '' ? Number(value) : null,
        notes: notes || undefined,
      });
      toast('Allokáció létrehozva.', 'success');
      onSaved();
    } catch (e: any) { toast(e?.message || 'Mentés sikertelen.', 'error'); }
    finally { setSaving(false); }
  };

  return (
    <Modal isOpen={true} onClose={onClose} title="+ Allokáció hozzáadása">
      <div className="space-y-4">
        {/* Line info */}
        <div style={{ background: 'var(--mist)', borderRadius: 'var(--r)', padding: '10px 14px', fontSize: '13px' }}>
          <div style={{ fontWeight: 700, color: 'var(--navy)' }}>{line.description}</div>
          <div style={{ color: 'var(--slate)', marginTop: '2px', display: 'flex', gap: '14px', flexWrap: 'wrap' }}>
            <span>Nettó: <strong>{fmt(line.line_net)}</strong></span>
            <span>Már allokálva: <strong>{fmt(alreadyAllocatedHuf)}</strong></span>
            <span>Szabad: <strong style={{ color: '#16a34a' }}>{fmt(remainingHuf)}</strong></span>
          </div>
        </div>

        {/* Project */}
        <div>
          <label className="form-label">Projekt *</label>
          <select value={projectId} onChange={e => setProjectId(e.target.value)} className="form-input">
            <option value="">Válassz projektet...</option>
            {projects.map(p => (
              <option key={p.id} value={p.id}>{p.name} ({p.project_code})</option>
            ))}
          </select>
        </div>

        {/* Mode */}
        <div>
          <label className="form-label">Allokáció módja</label>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px' }}>
            {[
              { v: 'full' as const, label: '🎯 Teljes (maradék)', sub: fmt(remainingHuf) },
              { v: 'percent' as const, label: '% Százalékos', sub: '0-100%' },
              { v: 'fixed_amount' as const, label: '💰 Fix összeg', sub: 'Ft-ban' },
              { v: 'quantity' as const, label: '📦 Mennyiség', sub: 'db' },
            ].map(opt => (
              <button key={opt.v} type="button"
                onClick={() => { setMode(opt.v); setValue(''); }}
                style={{
                  padding: '8px 10px', borderRadius: 'var(--r)',
                  border: `2px solid ${mode === opt.v ? 'var(--orange)' : 'var(--line)'}`,
                  background: mode === opt.v ? '#fff7ed' : '#fff',
                  color: mode === opt.v ? 'var(--orange)' : 'var(--ink)',
                  cursor: 'pointer', fontSize: '12px', textAlign: 'left', fontWeight: mode === opt.v ? 700 : 400,
                }}>
                <div>{opt.label}</div>
                <div style={{ fontSize: '10px', color: 'var(--slate)', marginTop: '2px' }}>{opt.sub}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Value input */}
        {mode !== 'full' && (
          <div>
            <label className="form-label">
              {mode === 'percent' ? 'Százalék (0-100)' : mode === 'fixed_amount' ? 'Fix összeg (Ft)' : 'Mennyiség (db)'}
            </label>
            <input
              type="number"
              value={value}
              onChange={e => setValue(e.target.value === '' ? '' : Number(e.target.value))}
              min={0}
              max={mode === 'percent' ? 100 : undefined}
              step={mode === 'percent' ? 0.01 : 1}
              className="form-input"
              placeholder={mode === 'percent' ? '0-100' : '0'}
              autoFocus
            />
          </div>
        )}

        {/* Live preview */}
        {previewHuf !== null && previewHuf > 0 && (
          <div style={{
            background: '#f0fdf4', border: '1px solid #bbf7d0',
            borderRadius: 'var(--r)', padding: '10px 14px', fontSize: '13px',
          }}>
            <div style={{ color: '#166534' }}>
              Allokált nettó összeg: <strong style={{ fontSize: '16px' }}>{fmt(previewHuf)}</strong>
            </div>
          </div>
        )}

        {mode === 'fixed_amount' && value !== '' && Number(value) > remainingHuf + 0.01 && (
          <p style={{ fontSize: '12px', color: '#dc2626' }}>A fix összeg meghaladja a szabad allokálható összeget.</p>
        )}

        <Input label="Megjegyzés (opcionális)" value={notes} onChange={e => setNotes(e.target.value)} placeholder="..." />
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', paddingTop: '14px', borderTop: '1px solid var(--line)', marginTop: '14px' }}>
        <Button variant="ghost" onClick={onClose}>Mégse</Button>
        <Button onClick={handleSave} loading={saving} disabled={!isValid()}>Allokálás</Button>
      </div>
    </Modal>
  );
}

// ── Time Proportional Modal ──

function TimeProportionalModal({ line, projects, onClose, onSaved, toast }: {
  line: InvoiceLine;
  projects: Project[];
  onClose: () => void;
  onSaved: () => void;
  toast: (m: string, v: string) => void;
}) {
  const [periodFrom, setPeriodFrom] = useState('');
  const [periodTo, setPeriodTo] = useState('');
  const [strategy, setStrategy] = useState<'overlap_days' | 'days' | 'months'>('overlap_days');
  const [candidateIds, setCandidateIds] = useState<string[]>([]);
  const [preview, setPreview] = useState<Array<{
    projectId: string; projectName: string; weightPercent: number;
    overlapDays: number; allocatedNet: number; allocatedGross: number;
  }> | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [saving, setSaving] = useState(false);

  const handlePreview = async () => {
    if (!periodFrom || !periodTo) { toast('Add meg az időszak elejét és végét.', 'error'); return; }
    setPreviewing(true);
    try {
      const result = await post<any>(`/incoming-invoice-lines/${line.id}/time-proportional/preview`, {
        periodFrom, periodTo, weightingStrategy: strategy,
        candidateProjectIds: candidateIds.length > 0 ? candidateIds : undefined,
      });
      setPreview(result.allocations || []);
    } catch (e: any) { toast(e?.message || 'Előnézet sikertelen.', 'error'); }
    finally { setPreviewing(false); }
  };

  const handleSave = async () => {
    if (!preview?.length) { toast('Előbb generálj előnézetet.', 'error'); return; }
    setSaving(true);
    try {
      await post(`/incoming-invoice-lines/${line.id}/time-proportional`, {
        periodFrom, periodTo, weightingStrategy: strategy,
        candidateProjectIds: candidateIds.length > 0 ? candidateIds : undefined,
      });
      toast('Időarányos allokáció létrehozva.', 'success');
      onSaved();
    } catch (e: any) { toast(e?.message || 'Mentés sikertelen.', 'error'); }
    finally { setSaving(false); }
  };

  return (
    <Modal isOpen={true} onClose={onClose} title="⏱ Időarányos elosztás" size="lg">
      <div className="space-y-4">
        <div style={{ background: 'var(--mist)', borderRadius: 'var(--r)', padding: '10px 14px', fontSize: '13px' }}>
          <div style={{ fontWeight: 700, color: 'var(--navy)' }}>{line.description}</div>
          <div style={{ color: 'var(--slate)' }}>Nettó: {fmt(line.line_net)} | Bruttó: {fmt(line.line_gross)}</div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
          <div>
            <label className="form-label">Periódus kezdete *</label>
            <input type="date" value={periodFrom}
              onChange={e => { setPeriodFrom(e.target.value); setPreview(null); }}
              className="form-input" />
          </div>
          <div>
            <label className="form-label">Periódus vége *</label>
            <input type="date" value={periodTo}
              onChange={e => { setPeriodTo(e.target.value); setPreview(null); }}
              className="form-input" />
          </div>
        </div>

        <div>
          <label className="form-label">Súlyozási stratégia</label>
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
            {[
              { v: 'overlap_days' as const, label: 'Átfedő napok', desc: 'Projekt és periódus metszete' },
              { v: 'days' as const, label: 'Teljes napok', desc: 'Projekt teljes futamideje' },
              { v: 'months' as const, label: 'Hónaparányos', desc: 'Részleges hónap is számít' },
            ].map(s => (
              <button key={s.v} type="button"
                onClick={() => { setStrategy(s.v); setPreview(null); }}
                style={{
                  padding: '8px 12px', borderRadius: 'var(--r)',
                  border: `2px solid ${strategy === s.v ? 'var(--orange)' : 'var(--line)'}`,
                  background: strategy === s.v ? '#fff7ed' : '#fff',
                  color: strategy === s.v ? 'var(--orange)' : 'var(--ink)',
                  cursor: 'pointer', fontSize: '12px', textAlign: 'left', fontWeight: strategy === s.v ? 700 : 400,
                }}>
                <div>{s.label}</div>
                <div style={{ fontSize: '10px', color: 'var(--slate)' }}>{s.desc}</div>
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="form-label">Projektek szűrése (üresen = automatikus detekció)</label>
          <div style={{ display: 'grid', gap: '3px', maxHeight: '140px', overflowY: 'auto', border: '1px solid var(--line)', borderRadius: 'var(--r)', padding: '6px 10px' }}>
            {projects.map(p => (
              <label key={p.id} style={{ display: 'flex', gap: '7px', cursor: 'pointer', fontSize: '13px', alignItems: 'center', padding: '2px 0' }}>
                <input type="checkbox" checked={candidateIds.includes(p.id)}
                  onChange={e => {
                    setCandidateIds(prev =>
                      e.target.checked ? [...prev, p.id] : prev.filter(id => id !== p.id),
                    );
                    setPreview(null);
                  }} />
                <span>{p.name} <span style={{ color: 'var(--slate)', fontSize: '11px' }}>({p.project_code})</span></span>
              </label>
            ))}
          </div>
        </div>

        <Button variant="ghost" onClick={handlePreview} loading={previewing} disabled={!periodFrom || !periodTo}>
          Előnézet generálása
        </Button>

        {preview && preview.length > 0 && (
          <div style={{ border: '1px solid var(--line)', borderRadius: 'var(--r)', overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
              <thead>
                <tr style={{ background: 'var(--navy)', color: '#fff' }}>
                  <th style={{ padding: '8px 12px', textAlign: 'left' }}>Projekt</th>
                  <th style={{ padding: '8px 12px', textAlign: 'right' }}>Átfedő nap</th>
                  <th style={{ padding: '8px 12px', textAlign: 'right' }}>Súly %</th>
                  <th style={{ padding: '8px 12px', textAlign: 'right' }}>Allokált nettó</th>
                </tr>
              </thead>
              <tbody>
                {preview.map((item, i) => (
                  <tr key={item.projectId} style={{ background: i % 2 === 0 ? '#fff' : 'var(--mist)' }}>
                    <td style={{ padding: '8px 12px', fontWeight: 600, color: 'var(--navy)' }}>{item.projectName}</td>
                    <td style={{ padding: '8px 12px', textAlign: 'right', color: 'var(--slate)' }}>{item.overlapDays}</td>
                    <td style={{ padding: '8px 12px', textAlign: 'right' }}>{Math.round(item.weightPercent * 10) / 10}%</td>
                    <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 700, color: '#16a34a' }}>
                      {fmt(Math.round(item.allocatedNet / 100))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {preview && preview.length === 0 && (
          <div style={{ textAlign: 'center', padding: '16px', color: 'var(--slate)', fontSize: '13px' }}>
            Az időszakra nincs átfedő aktív projekt.
          </div>
        )}
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', paddingTop: '14px', borderTop: '1px solid var(--line)', marginTop: '14px' }}>
        <Button variant="ghost" onClick={onClose}>Mégse</Button>
        <Button onClick={handleSave} loading={saving} disabled={!preview?.length}>
          Allokálás ({preview?.length ?? 0} projektre)
        </Button>
      </div>
    </Modal>
  );
}

// ════════════════════════════════════════════
// Tab 1 — Szabályok
// ════════════════════════════════════════════

function RulesTab({ toast, projects }: {
  toast: (m: string, v: string) => void;
  projects: Project[];
}) {
  const [rules, setRules] = useState<AllocationRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [editRule, setEditRule] = useState<AllocationRule | null>(null);
  const [showModal, setShowModal] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await get<any>('/allocation-rules');
      const list = Array.isArray(d) ? d : [];
      setRules(list.sort((a: AllocationRule, b: AllocationRule) => a.sort_order - b.sort_order));
    } catch { /* silent */ }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, []);

  const moveRule = async (idx: number, dir: -1 | 1) => {
    const next = [...rules];
    const target = idx + dir;
    if (target < 0 || target >= next.length) return;
    [next[idx], next[target]] = [next[target], next[idx]];
    setRules(next);
    try {
      await post(`/allocation-rules/${next[target].id}/reorder`, { orderedIds: next.map(r => r.id) });
    } catch { toast('Sorrend módosítása sikertelen.', 'error'); load(); }
  };

  const dryRun = async (rule: AllocationRule) => {
    try {
      const result = await post<any>(`/allocation-rules/${rule.id}/dry-run`, {});
      toast(
        `"${rule.name}": ${result.matchCount ?? 0} számlasor illeszkedik, ${result.wouldAllocate ?? result.matchCount ?? 0} allokáció jönne létre.`,
        'info',
      );
    } catch (e: any) { toast(e?.message || 'Dry-run sikertelen.', 'error'); }
  };

  const reapply = async (rule: AllocationRule) => {
    if (!confirm(`Újra alkalmazod a "${rule.name}" szabályt? A meglévő auto-allokációk felülíródhatnak.`)) return;
    try {
      const result = await post<any>(`/allocation-rules/${rule.id}/reapply`, {});
      toast(`"${rule.name}": ${result.applied ?? 0} allokáció létrehozva/frissítve.`, 'success');
    } catch (e: any) { toast(e?.message || 'Újraalkalmazás sikertelen.', 'error'); }
  };

  if (loading) return (
    <div style={{ textAlign: 'center', padding: '40px', color: 'var(--slate)' }}>Betöltés...</div>
  );

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
        <p style={{ fontSize: '13px', color: 'var(--slate)', margin: 0 }}>
          A szabályok sorrendben érvényesülnek (első illeszkedő nyer). Feltételtől függően automatikusan allokálnak.
        </p>
        <Button onClick={() => { setEditRule(null); setShowModal(true); }}>+ Új szabály</Button>
      </div>

      {rules.length === 0 ? (
        <EmptyState
          title="Nincs allokációs szabály"
          description="Hozz létre szabályokat az automatikus allokációhoz. A szabályok az új bejövő számlákon futnak le."
          action={<Button onClick={() => { setEditRule(null); setShowModal(true); }}>+ Első szabály</Button>}
        />
      ) : (
        <div style={{ display: 'grid', gap: '8px' }}>
          {rules.map((r, i) => (
            <Card key={r.id} style={{
              padding: '12px 16px',
              borderLeft: `4px solid ${r.active ? 'var(--orange)' : 'var(--line)'}`,
              opacity: r.active ? 1 : 0.6,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                {/* Reorder buttons */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1px', flexShrink: 0 }}>
                  <button onClick={() => moveRule(i, -1)} disabled={i === 0}
                    style={{ background: 'none', border: 'none', cursor: i === 0 ? 'default' : 'pointer', color: i === 0 ? 'var(--line)' : 'var(--slate)', fontSize: '11px', lineHeight: 1, padding: '2px' }}>▲</button>
                  <button onClick={() => moveRule(i, 1)} disabled={i === rules.length - 1}
                    style={{ background: 'none', border: 'none', cursor: i === rules.length - 1 ? 'default' : 'pointer', color: i === rules.length - 1 ? 'var(--line)' : 'var(--slate)', fontSize: '11px', lineHeight: 1, padding: '2px' }}>▼</button>
                </div>

                <div style={{ width: '24px', height: '24px', borderRadius: '50%', background: 'var(--mist)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: 700, color: 'var(--slate)', flexShrink: 0 }}>
                  {i + 1}
                </div>

                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: '14px', color: 'var(--navy)' }}>{r.name}</div>
                  <div style={{ fontSize: '12px', color: 'var(--slate)', marginTop: '2px' }}>
                    Feltétel: {COND_LABELS[r.condition_type] || r.condition_type}
                    {r.condition_value ? ` = "${r.condition_value}"` : ''}
                    {r.targets?.length > 0 && (
                      <span> → {r.targets.map(t => `${t.target_name || t.target_id} (${t.percentage ?? ''}%)`).join(', ')}</span>
                    )}
                  </div>
                </div>

                <Badge variant={r.active ? 'success' : 'default'}>{r.active ? 'Aktív' : 'Inaktív'}</Badge>

                <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                  <Button size="sm" variant="ghost" onClick={() => dryRun(r)} title="Próba-futtatás">Dry-run</Button>
                  <Button size="sm" variant="ghost" onClick={() => reapply(r)} title="Újra alkalmaz meglévő számlákra">Újra alkalmaz</Button>
                  <Button size="sm" variant="ghost" onClick={() => { setEditRule(r); setShowModal(true); }}>Szerkesztés</Button>
                  <Button size="sm" variant="ghost" style={{ color: '#dc2626' }} onClick={async () => {
                    if (!confirm(`Törlöd a "${r.name}" szabályt?`)) return;
                    try { await del(`/allocation-rules/${r.id}`); toast('Szabály törölve.', 'success'); load(); }
                    catch { toast('Törlés sikertelen.', 'error'); }
                  }}>Törlés</Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {showModal && (
        <RuleModal
          rule={editRule}
          projects={projects}
          onClose={() => setShowModal(false)}
          onSaved={() => { setShowModal(false); load(); }}
          toast={toast}
        />
      )}
    </div>
  );
}

// ── Rule Modal ──

function RuleModal({ rule, projects, onClose, onSaved, toast }: {
  rule: AllocationRule | null;
  projects: Project[];
  onClose: () => void;
  onSaved: () => void;
  toast: (m: string, v: string) => void;
}) {
  const [name, setName] = useState(rule?.name || '');
  const [condType, setCondType] = useState(rule?.condition_type || 'always');
  const [condValue, setCondValue] = useState(rule?.condition_value || '');
  const [active, setActive] = useState(rule?.active ?? true);
  const [targets, setTargets] = useState<{ projectId: string; percentage: number }[]>(
    rule?.targets?.map(t => ({ projectId: t.target_id, percentage: t.percentage || 0 })) ||
    [{ projectId: '', percentage: 100 }],
  );
  const [saving, setSaving] = useState(false);

  const totalPct = targets.reduce((s, t) => s + (t.percentage || 0), 0);
  const pctOk = Math.abs(totalPct - 100) < 0.01;

  const handleSave = async () => {
    if (!name.trim()) { toast('Adj meg nevet.', 'error'); return; }
    if (condType !== 'always' && !condValue.trim()) { toast('Add meg a feltétel értékét.', 'error'); return; }
    if (targets.some(t => !t.projectId)) { toast('Minden célnál válassz projektet.', 'error'); return; }
    if (!pctOk) { toast('A célok százalékainak összege 100% kell legyen.', 'error'); return; }
    setSaving(true);
    try {
      const body = {
        name: name.trim(),
        conditionType: condType,
        conditionValue: condType !== 'always' ? condValue.trim() : undefined,
        active,
        targets,
      };
      if (rule) {
        await patch(`/allocation-rules/${rule.id}`, body);
        toast('Szabály frissítve.', 'success');
      } else {
        await post('/allocation-rules', body);
        toast('Szabály létrehozva.', 'success');
      }
      onSaved();
    } catch (e: any) { toast(e?.message || 'Mentés sikertelen.', 'error'); }
    finally { setSaving(false); }
  };

  return (
    <Modal isOpen={true} onClose={onClose} title={rule ? 'Szabály szerkesztése' : '+ Új allokációs szabály'}>
      <div className="space-y-4">
        <Input label="Név *" value={name} onChange={e => setName(e.target.value)} placeholder="pl. Irodaköltség → Projektek" />

        <div>
          <label className="form-label">Mikor érvényesüljön (feltétel)</label>
          <div style={{ display: 'grid', gap: '5px' }}>
            {[
              { v: 'always', label: 'Mindig (minden új bejövő számlasor)' },
              { v: 'supplier', label: 'Beszállító neve tartalmaz...' },
              { v: 'description', label: 'Tárgy / leírás tartalmaz...' },
              { v: 'amount_range', label: 'Összeg-tartomány (pl. 10000-50000)' },
            ].map(opt => (
              <label key={opt.v} style={{ display: 'flex', gap: '7px', cursor: 'pointer', fontSize: '13px', alignItems: 'center' }}>
                <input type="radio" name="condType" checked={condType === opt.v} onChange={() => setCondType(opt.v)} />
                {opt.label}
              </label>
            ))}
          </div>
          {condType !== 'always' && (
            <Input
              value={condValue}
              onChange={e => setCondValue(e.target.value)}
              placeholder={
                condType === 'supplier' ? 'pl. Telekom...' :
                  condType === 'description' ? 'pl. internet, rezsi...' : '10000-50000'
              }
              style={{ marginTop: '8px' }}
            />
          )}
        </div>

        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
            <label className="form-label" style={{ margin: 0 }}>Hova allokáljon (célok)</label>
            <button type="button"
              onClick={() => setTargets([...targets, { projectId: '', percentage: 0 }])}
              style={{ fontSize: '12px', color: 'var(--orange)', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 700 }}>
              + Sor
            </button>
          </div>
          <div style={{ display: 'grid', gap: '6px' }}>
            {targets.map((t, i) => (
              <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 80px 28px', gap: '6px', alignItems: 'center' }}>
                <select
                  value={t.projectId}
                  onChange={e => { const n = [...targets]; n[i].projectId = e.target.value; setTargets(n); }}
                  className="form-input" style={{ fontSize: '13px' }}
                >
                  <option value="">Projekt...</option>
                  {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
                <input
                  type="number" value={t.percentage || ''} min={0} max={100} step={0.1}
                  onChange={e => { const n = [...targets]; n[i].percentage = Number(e.target.value); setTargets(n); }}
                  className="form-input" style={{ textAlign: 'right', fontSize: '13px' }} placeholder="%"
                />
                <button type="button"
                  onClick={() => setTargets(targets.filter((_, idx) => idx !== i))}
                  style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: '18px', lineHeight: 1 }}>×</button>
              </div>
            ))}
          </div>
          <div style={{ fontSize: '12px', textAlign: 'right', marginTop: '4px', color: pctOk ? '#16a34a' : '#dc2626', fontWeight: 600 }}>
            Összesen: {totalPct}% {pctOk ? '✓' : '(100% kell)'}
          </div>
        </div>

        <label style={{ display: 'flex', gap: '8px', alignItems: 'center', cursor: 'pointer', fontSize: '13px' }}>
          <input type="checkbox" checked={active} onChange={e => setActive(e.target.checked)} />
          Aktív (azonnal alkalmazza az új bejövő számlákra)
        </label>
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', paddingTop: '14px', borderTop: '1px solid var(--line)', marginTop: '14px' }}>
        <Button variant="ghost" onClick={onClose}>Mégse</Button>
        <Button onClick={handleSave} loading={saving}>{rule ? 'Mentés' : 'Létrehozás'}</Button>
      </div>
    </Modal>
  );
}

// ════════════════════════════════════════════
// Tab 2 — Sablonok
// ════════════════════════════════════════════

function TemplatesTab({ toast, projects }: {
  toast: (m: string, v: string) => void;
  projects: Project[];
}) {
  const [templates, setTemplates] = useState<AllocationTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [editTemplate, setEditTemplate] = useState<AllocationTemplate | null>(null);
  const [showModal, setShowModal] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await get<any>('/allocation-templates');
      setTemplates(Array.isArray(d) ? d : []);
    } catch { /* silent */ }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, []);

  if (loading) return (
    <div style={{ textAlign: 'center', padding: '40px', color: 'var(--slate)' }}>Betöltés...</div>
  );

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
        <p style={{ fontSize: '13px', color: 'var(--slate)', margin: 0 }}>
          A sablonok egyszerre több allokációt rögzítenek. Alkalmazhatók bármelyik számlasoron.
        </p>
        <Button onClick={() => { setEditTemplate(null); setShowModal(true); }}>+ Új sablon</Button>
      </div>

      {templates.length === 0 ? (
        <EmptyState
          title="Nincs sablon"
          description='Hozz létre sablonokat az ismétlődő allokációs minták gyors alkalmazásához. Pl.: "Irodaköltség = 40% A projekt + 60% B projekt".'
          action={<Button onClick={() => { setEditTemplate(null); setShowModal(true); }}>+ Első sablon</Button>}
        />
      ) : (
        <div style={{ display: 'grid', gap: '8px' }}>
          {templates.map(t => {
            const allocs = t.template_json?.allocations || [];
            return (
              <Card key={t.id} style={{ padding: '12px 16px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: '14px', color: 'var(--navy)' }}>{t.name}</div>
                    {t.description && (
                      <div style={{ fontSize: '12px', color: 'var(--slate)', marginTop: '2px' }}>{t.description}</div>
                    )}
                    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '8px' }}>
                      {allocs.map((a, i) => {
                        const proj = projects.find(p => p.id === a.projectId);
                        return (
                          <Badge key={i} variant="info">
                            {proj?.name || a.projectId}: {modeDisplay(a.mode, a.value ?? null)}
                          </Badge>
                        );
                      })}
                      {allocs.length === 0 && (
                        <span style={{ fontSize: '12px', color: 'var(--slate)', fontStyle: 'italic' }}>Üres sablon</span>
                      )}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '4px', flexShrink: 0 }}>
                    <Button size="sm" variant="ghost" onClick={() => { setEditTemplate(t); setShowModal(true); }}>Szerkesztés</Button>
                    <Button size="sm" variant="ghost" style={{ color: '#dc2626' }} onClick={async () => {
                      if (!confirm(`Törlöd a "${t.name}" sablont?`)) return;
                      try { await del(`/allocation-templates/${t.id}`); toast('Sablon törölve.', 'success'); load(); }
                      catch { toast('Törlés sikertelen.', 'error'); }
                    }}>Törlés</Button>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {showModal && (
        <TemplateModal
          template={editTemplate}
          projects={projects}
          onClose={() => setShowModal(false)}
          onSaved={() => { setShowModal(false); load(); }}
          toast={toast}
        />
      )}
    </div>
  );
}

// ── Template Modal ──

function TemplateModal({ template, projects, onClose, onSaved, toast }: {
  template: AllocationTemplate | null;
  projects: Project[];
  onClose: () => void;
  onSaved: () => void;
  toast: (m: string, v: string) => void;
}) {
  const [name, setName] = useState(template?.name || '');
  const [description, setDescription] = useState(template?.description || '');
  const [allocations, setAllocations] = useState<Array<{ projectId: string; mode: string; value: number | '' }>>(
    template?.template_json?.allocations?.map(a => ({
      projectId: a.projectId,
      mode: a.mode,
      value: a.value !== undefined ? a.value : '',
    })) || [{ projectId: '', mode: 'percent', value: 100 }],
  );
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!name.trim()) { toast('Adj meg nevet.', 'error'); return; }
    if (allocations.length === 0) { toast('Adj meg legalább egy allokációt.', 'error'); return; }
    if (allocations.some(a => !a.projectId)) { toast('Minden sornál válassz projektet.', 'error'); return; }
    setSaving(true);
    try {
      const templateJson = {
        allocations: allocations.map(a => ({
          projectId: a.projectId,
          mode: a.mode,
          ...(a.mode !== 'full' && a.value !== '' ? { value: Number(a.value) } : {}),
        })),
      };
      if (template) {
        await patch(`/allocation-templates/${template.id}`, {
          name: name.trim(), description: description || undefined, templateJson,
        });
        toast('Sablon frissítve.', 'success');
      } else {
        await post('/allocation-templates', {
          name: name.trim(), description: description || undefined, templateJson,
        });
        toast('Sablon létrehozva.', 'success');
      }
      onSaved();
    } catch (e: any) { toast(e?.message || 'Mentés sikertelen.', 'error'); }
    finally { setSaving(false); }
  };

  return (
    <Modal isOpen={true} onClose={onClose} title={template ? 'Sablon szerkesztése' : '+ Új allokációs sablon'}>
      <div className="space-y-4">
        <Input label="Neve *" value={name} onChange={e => setName(e.target.value)} placeholder="pl. Irodaköltség elosztás" />
        <Input label="Leírás (opcionális)" value={description} onChange={e => setDescription(e.target.value)} placeholder="Mikor használd ezt a sablont..." />

        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
            <label className="form-label" style={{ margin: 0 }}>Allokációk</label>
            <button type="button"
              onClick={() => setAllocations([...allocations, { projectId: '', mode: 'percent', value: 0 }])}
              style={{ fontSize: '12px', color: 'var(--orange)', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 700 }}>
              + Sor
            </button>
          </div>
          <div style={{ display: 'grid', gap: '6px' }}>
            {allocations.map((a, i) => (
              <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 120px 80px 28px', gap: '6px', alignItems: 'center' }}>
                <select
                  value={a.projectId}
                  onChange={e => { const n = [...allocations]; n[i].projectId = e.target.value; setAllocations(n); }}
                  className="form-input" style={{ fontSize: '13px' }}
                >
                  <option value="">Projekt...</option>
                  {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
                <select
                  value={a.mode}
                  onChange={e => {
                    const n = [...allocations];
                    n[i].mode = e.target.value;
                    if (e.target.value === 'full') n[i].value = '';
                    setAllocations(n);
                  }}
                  className="form-input" style={{ fontSize: '13px' }}
                >
                  <option value="full">Teljes</option>
                  <option value="percent">Százalék</option>
                  <option value="fixed_amount">Fix Ft</option>
                  <option value="quantity">Mennyiség</option>
                </select>
                <input
                  type="number" value={a.value} disabled={a.mode === 'full'}
                  onChange={e => { const n = [...allocations]; n[i].value = e.target.value === '' ? '' : Number(e.target.value); setAllocations(n); }}
                  className="form-input" style={{ textAlign: 'right', fontSize: '13px' }} placeholder="érték"
                />
                <button type="button"
                  onClick={() => setAllocations(allocations.filter((_, idx) => idx !== i))}
                  style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: '18px', lineHeight: 1 }}>×</button>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', paddingTop: '14px', borderTop: '1px solid var(--line)', marginTop: '14px' }}>
        <Button variant="ghost" onClick={onClose}>Mégse</Button>
        <Button onClick={handleSave} loading={saving}>{template ? 'Mentés' : 'Létrehozás'}</Button>
      </div>
    </Modal>
  );
}

// ════════════════════════════════════════════
// Tab 3 — Projektköltség-jelentés
// ════════════════════════════════════════════

function ReportTab({ toast, projects }: {
  toast: (m: string, v: string) => void;
  projects: Project[];
}) {
  const [selectedProject, setSelectedProject] = useState('');
  const [report, setReport] = useState<CostReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [overview, setOverview] = useState<Array<{ id: string; name: string; status: string; revenueNet: number; totalCost: number; margin: number | null }>>([]);
  const [overviewLoading, setOverviewLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [showOverview, setShowOverview] = useState(true);

  useEffect(() => {
    setOverviewLoading(true);
    get<any>('/cost-reports/projects')
      .then(d => setOverview(Array.isArray(d) ? d : []))
      .catch(() => {})
      .finally(() => setOverviewLoading(false));
  }, []);

  const loadReport = useCallback(async (projectId: string) => {
    if (!projectId) { setReport(null); return; }
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (dateFrom) params.set('from_date', dateFrom);
      if (dateTo) params.set('to_date', dateTo);
      const qs = params.toString();
      const data = await get<CostReport>(`/projects/${projectId}/cost-report${qs ? '?' + qs : ''}`);
      setReport(data);
    } catch { toast('Jelentés betöltése sikertelen.', 'error'); setReport(null); }
    finally { setLoading(false); }
  }, [dateFrom, dateTo]);

  const selectProject = (id: string) => {
    setSelectedProject(id);
    setShowOverview(false);
    loadReport(id);
  };

  return (
    <div>
      {/* Controls */}
      <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-end', marginBottom: '20px', flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: '200px' }}>
          <label className="form-label">Projekt</label>
          <select value={selectedProject} onChange={e => selectProject(e.target.value)} className="form-input">
            <option value="">Válassz projektet...</option>
            {projects.map(p => <option key={p.id} value={p.id}>{p.name} ({p.project_code})</option>)}
          </select>
        </div>
        <div>
          <label className="form-label">Időszak kezdete</label>
          <input type="date" value={dateFrom}
            onChange={e => { setDateFrom(e.target.value); if (selectedProject) loadReport(selectedProject); }}
            className="form-input" style={{ width: '136px' }} />
        </div>
        <div>
          <label className="form-label">Vége</label>
          <input type="date" value={dateTo}
            onChange={e => { setDateTo(e.target.value); if (selectedProject) loadReport(selectedProject); }}
            className="form-input" style={{ width: '136px' }} />
        </div>
        {selectedProject && (
          <div style={{ display: 'flex', gap: '5px' }}>
            <a href={`/api/projects/${selectedProject}/cost-report/export.pdf`} target="_blank" rel="noreferrer" style={{ textDecoration: 'none' }}>
              <Button size="sm" variant="ghost">📄 PDF</Button>
            </a>
            <a href={`/api/projects/${selectedProject}/cost-report/export.xlsx`} target="_blank" rel="noreferrer" style={{ textDecoration: 'none' }}>
              <Button size="sm" variant="ghost">📊 XLSX</Button>
            </a>
            <a href={`/api/projects/${selectedProject}/cost-report/export.csv`} target="_blank" rel="noreferrer" style={{ textDecoration: 'none' }}>
              <Button size="sm" variant="ghost">📋 CSV</Button>
            </a>
          </div>
        )}
        <button
          onClick={() => setShowOverview(o => !o)}
          style={{ background: 'none', border: 'none', color: 'var(--orange)', cursor: 'pointer', fontSize: '13px', fontWeight: 600, alignSelf: 'flex-end', paddingBottom: '8px' }}>
          {showOverview ? '▲ Áttekintő bezárása' : '▼ Összes projekt'}
        </button>
      </div>

      {/* Projects overview table */}
      {showOverview && (
        <div style={{ marginBottom: '24px' }}>
          <h3 style={{ fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--slate)', marginBottom: '10px' }}>
            Összes projekt — költség áttekintése
          </h3>
          {overviewLoading ? (
            <div style={{ textAlign: 'center', padding: '20px', color: 'var(--slate)' }}>Betöltés...</div>
          ) : overview.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '20px', color: 'var(--slate)', fontSize: '13px' }}>Nincs projekt.</div>
          ) : (
            <Card>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid var(--line)' }}>
                      <th style={{ textAlign: 'left', padding: '10px 14px', color: 'var(--slate)', fontWeight: 600 }}>Projekt</th>
                      <th style={{ textAlign: 'right', padding: '10px 14px', color: 'var(--slate)', fontWeight: 600 }}>Bevétel (nettó)</th>
                      <th style={{ textAlign: 'right', padding: '10px 14px', color: 'var(--slate)', fontWeight: 600 }}>Összes költség</th>
                      <th style={{ textAlign: 'right', padding: '10px 14px', color: 'var(--slate)', fontWeight: 600 }}>Árrés %</th>
                    </tr>
                  </thead>
                  <tbody>
                    {overview.map((r, i) => (
                      <tr key={r.id}
                        style={{ borderBottom: '1px solid var(--line)', background: i % 2 === 0 ? '#fff' : 'var(--mist)', cursor: 'pointer' }}
                        onClick={() => selectProject(r.id)}
                        onMouseEnter={e => (e.currentTarget.style.background = '#fff7ed')}
                        onMouseLeave={e => (e.currentTarget.style.background = i % 2 === 0 ? '#fff' : 'var(--mist)')}
                      >
                        <td style={{ padding: '10px 14px', fontWeight: 600, color: 'var(--navy)' }}>{r.name}</td>
                        <td style={{ padding: '10px 14px', textAlign: 'right' }}>{fmt(r.revenueNet / 100)}</td>
                        <td style={{ padding: '10px 14px', textAlign: 'right' }}>{fmt(r.totalCost / 100)}</td>
                        <td style={{ padding: '10px 14px', textAlign: 'right' }}>
                          {r.margin !== null ? (
                            <span style={{ color: r.margin >= 0 ? '#16a34a' : '#dc2626', fontWeight: 700 }}>
                              {r.margin.toFixed(1)}%
                            </span>
                          ) : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}
        </div>
      )}

      {/* Prompt to select project */}
      {!selectedProject && !showOverview && (
        <div style={{ textAlign: 'center', padding: '40px', color: 'var(--slate)', fontSize: '14px' }}>
          Válassz projektet a részletes költségjelentéshez.
        </div>
      )}

      {loading && (
        <div style={{ textAlign: 'center', padding: '40px', color: 'var(--slate)' }}>Betöltés...</div>
      )}

      {/* Detailed report */}
      {report && !loading && (
        <div>
          {/* KPI row */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '10px', marginBottom: '20px' }}>
            <KpiCard label="Bevétel (nettó)" value={fmt(report.revenue.totalNet)} color="var(--navy)" />
            <KpiCard label="Bevétel (bruttó)" value={fmt(report.revenue.totalGross)} color="var(--navy)" />
            <KpiCard label="Allokált költség" value={fmt(report.costs.directAllocated.net)} color="var(--orange)" />
            <KpiCard label="Munkaidő-költség" value={fmt(report.costs.laborCost.totalCost)} color="#7c3aed" />
            <KpiCard label="Összes költség" value={fmt(report.costs.totalCost)} color="#dc2626" />
            <KpiCard
              label="Árrés"
              value={report.margin.amount !== null ? fmt(report.margin.amount) : '—'}
              sub={report.margin.percent !== null ? `${report.margin.percent}%` : undefined}
              color={report.margin.percent !== null && report.margin.percent >= 0 ? '#16a34a' : '#dc2626'}
            />
          </div>

          {/* Revenue breakdown */}
          {report.revenue.byInvoice.length > 0 && (
            <SectionTable
              title="Bevétel-tételek (kimenő számlák)"
              columns={['Számla', 'Nettó', 'Bruttó']}
              rows={report.revenue.byInvoice.map(inv => [
                inv.invoiceNumber,
                fmt(inv.net),
                fmt(inv.gross),
              ])}
              aligns={['left', 'right', 'right']}
            />
          )}

          {/* Cost allocation breakdown */}
          {report.costs.directAllocated.byIncomingInvoice.length > 0 && (
            <div style={{ marginBottom: '20px' }}>
              <h4 style={{ fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--slate)', marginBottom: '8px' }}>
                Allokált közvetlen költségek (bejövő számlák)
              </h4>
              <Card>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid var(--line)' }}>
                        <th style={{ textAlign: 'left', padding: '8px 12px', color: 'var(--slate)', fontWeight: 600 }}>Forrás</th>
                        <th style={{ textAlign: 'left', padding: '8px 12px', color: 'var(--slate)', fontWeight: 600 }}>Tétel leírása</th>
                        <th style={{ textAlign: 'center', padding: '8px 12px', color: 'var(--slate)', fontWeight: 600 }}>Mód</th>
                        <th style={{ textAlign: 'right', padding: '8px 12px', color: 'var(--slate)', fontWeight: 600 }}>Allokált nettó</th>
                      </tr>
                    </thead>
                    <tbody>
                      {report.costs.directAllocated.byIncomingInvoice.flatMap((inv, ii) =>
                        inv.allocations.map((a, ai) => (
                          <tr key={`${ii}-${ai}`} style={{ borderBottom: '1px solid var(--line)' }}>
                            <td style={{ padding: '8px 12px', fontWeight: ai === 0 ? 600 : 400, color: ai === 0 ? 'var(--navy)' : 'transparent' }}>
                              {ai === 0 ? `${inv.supplierName}${inv.invoiceNumber ? ' / ' + inv.invoiceNumber : ''}` : ''}
                            </td>
                            <td style={{ padding: '8px 12px', color: 'var(--ink)' }}>{a.lineDescription}</td>
                            <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                              <Badge variant="default">{MODE_LABELS[a.mode] || a.mode}</Badge>
                            </td>
                            <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 700 }}>
                              {fmt(a.amount / 100)}
                            </td>
                          </tr>
                        )),
                      )}
                      <tr style={{ background: 'var(--mist)', fontWeight: 700 }}>
                        <td colSpan={3} style={{ padding: '8px 12px', color: 'var(--slate)', textAlign: 'right' }}>Összesen (nettó):</td>
                        <td style={{ padding: '8px 12px', textAlign: 'right', color: 'var(--orange)' }}>{fmt(report.costs.directAllocated.net)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </Card>
            </div>
          )}

          {/* Labor cost breakdown */}
          {report.costs.laborCost.byUser.length > 0 && (
            <div style={{ marginBottom: '20px' }}>
              <h4 style={{ fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--slate)', marginBottom: '8px' }}>
                Munkaidő-költség ({report.costs.laborCost.hours} h összesen, {report.costs.laborCost.billableHours} h számlázható)
              </h4>
              <Card>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid var(--line)' }}>
                        <th style={{ textAlign: 'left', padding: '8px 12px', color: 'var(--slate)', fontWeight: 600 }}>Felhasználó</th>
                        <th style={{ textAlign: 'right', padding: '8px 12px', color: 'var(--slate)', fontWeight: 600 }}>Óra</th>
                        <th style={{ textAlign: 'right', padding: '8px 12px', color: 'var(--slate)', fontWeight: 600 }}>Munkaidő-költség</th>
                      </tr>
                    </thead>
                    <tbody>
                      {report.costs.laborCost.byUser.map((u, i) => (
                        <tr key={i} style={{ borderBottom: '1px solid var(--line)' }}>
                          <td style={{ padding: '8px 12px', color: 'var(--navy)', fontWeight: 500 }}>{u.userName}</td>
                          <td style={{ padding: '8px 12px', textAlign: 'right', color: 'var(--slate)' }}>{u.hours} h</td>
                          <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 700 }}>{fmt(u.cost)}</td>
                        </tr>
                      ))}
                      <tr style={{ background: 'var(--mist)', fontWeight: 700 }}>
                        <td style={{ padding: '8px 12px', color: 'var(--slate)', textAlign: 'right' }} colSpan={2}>Összesen:</td>
                        <td style={{ padding: '8px 12px', textAlign: 'right', color: '#7c3aed' }}>{fmt(report.costs.laborCost.totalCost)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </Card>
            </div>
          )}

          <div style={{ fontSize: '11px', color: 'var(--slate)', textAlign: 'right', marginTop: '8px' }}>
            Generálva: {new Date(report.generatedAt).toLocaleString('hu-HU')} | {report.project.name}
          </div>
        </div>
      )}
    </div>
  );
}

// ── KPI Card ──

function KpiCard({ label, value, sub, color }: {
  label: string; value: string; sub?: string; color: string;
}) {
  return (
    <div style={{
      background: '#fff', border: '1px solid var(--line)', borderRadius: 'var(--r)',
      padding: '14px 16px', borderTop: `3px solid ${color}`,
    }}>
      <div style={{ fontSize: '10px', color: 'var(--slate)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '6px' }}>
        {label}
      </div>
      <div style={{ fontSize: '19px', fontWeight: 800, color }}>{value}</div>
      {sub && <div style={{ fontSize: '12px', color: 'var(--slate)', marginTop: '2px' }}>{sub}</div>}
    </div>
  );
}

// ── Section Table ──

function SectionTable({ title, columns, rows, aligns }: {
  title: string;
  columns: string[];
  rows: string[][];
  aligns: Array<'left' | 'right' | 'center'>;
}) {
  if (rows.length === 0) return null;
  return (
    <div style={{ marginBottom: '20px' }}>
      <h4 style={{ fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--slate)', marginBottom: '8px' }}>
        {title}
      </h4>
      <Card>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--line)' }}>
                {columns.map((col, i) => (
                  <th key={i} style={{ textAlign: aligns[i], padding: '8px 12px', color: 'var(--slate)', fontWeight: 600 }}>{col}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, ri) => (
                <tr key={ri} style={{ borderBottom: '1px solid var(--line)' }}>
                  {row.map((cell, ci) => (
                    <td key={ci} style={{ padding: '8px 12px', textAlign: aligns[ci], color: ci === 0 ? 'var(--navy)' : 'var(--ink)', fontWeight: ci === 0 ? 600 : 400 }}>
                      {cell}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
